import { useLang } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import { UserCheck, Wrench, Package, Bike, ArrowRight } from "lucide-react";
import ServiceLinksBlock from "../components/ServiceLinksBlock";

const TEAM_IMG = "https://customer-assets.emergentagent.com/job_ls39-bikes/artifacts/m249fgjj_ls39%20taller.webp";

export default function About() {
    const { lang } = useLang();

    const t = lang === "es" ? {
        overline: "Equipo LS39.PRO · Yamaha Mallorca",
        title: "Conoce a nuestro equipo:",
        title2: "pasión y experiencia Yamaha en Mallorca",
        intro: "En LS39 Motos, tu concesionario oficial Yamaha, no solo trabajamos con motocicletas: vivimos las dos ruedas. Nuestro equipo está integrado por profesionales apasionados y especialistas certificados, unidos por una única misión: ofrecerte una experiencia de compra y un servicio posventa de primer nivel.",
        trust_overline: "¿Por qué confiar en nuestro Equipo Experto?",
        trust_intro: "Para nosotros, la excelencia no es opcional. Estas son las ventajas de poner tu pasión en manos de nuestros profesionales:",
        t1_title: "Asesoramiento 100% Personalizado",
        t1_desc: "Te ayudamos a elegir la Yamaha perfecta para tu estilo de vida, explicándote al detalle cada innovación técnica y tecnológica.",
        t2_title: "Servicio Técnico de Alta Competición",
        t2_desc: "Contamos con mecánicos altamente cualificados y en constante formación, garantizando que tu moto reciba el mantenimiento óptimo bajo los estándares más estrictos de la marca.",
        t3_title: "Accesorios y Equipamiento Oficial",
        t3_desc: "Te equipamos a ti y a tu moto con componentes originales que aseguran el máximo rendimiento y seguridad en carretera.",
        t4_title: "Experiencias sobre Ruedas",
        t4_desc: "Llevamos la teoría a la práctica organizando eventos exclusivos y jornadas de pruebas de conducción (test rides) para que sientas el ADN de Yamaha en primera persona.",
        philosophy: "Un equipo experto no es solo el que sabe reparar una máquina, sino el que entiende la conexión entre el piloto y la carretera.",
        cta_title: "¡Lleva tu experiencia al siguiente nivel!",
        cta_desc: "Pásate por nuestras instalaciones estratégicas en la isla y descubre lo que el verdadero compromiso con la excelencia puede hacer por ti y por tu moto.",
        cta_btn: "Reservar cita en taller",
    } : {
        overline: "LS39.PRO Team · Yamaha Mallorca",
        title: "Meet our team:",
        title2: "passion and Yamaha expertise in Mallorca",
        intro: "At LS39 Motos, your official Yamaha dealership, we don't just work with motorcycles: we live two wheels. Our team is made up of passionate professionals and certified specialists, united by a single mission: to offer you a first-class buying experience and after-sales service.",
        trust_overline: "Why trust our Expert Team?",
        trust_intro: "For us, excellence is not optional. These are the advantages of putting your passion in the hands of our professionals:",
        t1_title: "100% Personalised Advice",
        t1_desc: "We help you choose the perfect Yamaha for your lifestyle, explaining every technical innovation in detail.",
        t2_title: "High-Performance Technical Service",
        t2_desc: "We have highly qualified mechanics in constant training, ensuring your bike receives optimum maintenance under the strictest brand standards.",
        t3_title: "Official Accessories & Equipment",
        t3_desc: "We equip you and your bike with original components that ensure maximum performance and road safety.",
        t4_title: "Experiences on Wheels",
        t4_desc: "We put theory into practice by organising exclusive events and test ride days so you can feel Yamaha's DNA first-hand.",
        philosophy: "An expert team is not just one that knows how to repair a machine, but one that understands the connection between rider and road.",
        cta_title: "Take your experience to the next level!",
        cta_desc: "Visit our strategically located facilities on the island and discover what true commitment to excellence can do for you and your bike.",
        cta_btn: "Book workshop appointment",
    };

    const pillars = [
        { icon: UserCheck, title: t.t1_title, desc: t.t1_desc },
        { icon: Wrench, title: t.t2_title, desc: t.t2_desc },
        { icon: Package, title: t.t3_title, desc: t.t3_desc },
        { icon: Bike, title: t.t4_title, desc: t.t4_desc },
    ];

    return (
        <div data-testid="about-page">
            {/* HERO */}
            <section className="relative isolate overflow-hidden border-b border-black/10">
                <div className="absolute inset-0 -z-10">
                    <img src={TEAM_IMG} alt="LS39 team" className="h-full w-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-[--ls-bg]/60 via-[--ls-bg]/40 to-[--ls-bg]" />
                </div>
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
                    <p className="text-overline">{t.overline}</p>
                    <h1 className="mt-6 font-display text-5xl font-bold tracking-tighter sm:text-6xl lg:text-7xl">
                        {t.title}
                        <br />
                        <span style={{ color: "var(--ls-terracotta)" }}>{t.title2}</span>
                    </h1>
                </div>
            </section>

            {/* INTRO */}
            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10 lg:py-28">
                <p className="text-base text-neutral-700 leading-relaxed sm:text-lg">{t.intro}</p>
            </section>

            {/* 4 PILLARS */}
            <section className="border-y border-black/10 bg-[--ls-paper]">
                <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
                    <p className="text-overline">{t.trust_overline}</p>
                    <p className="mt-4 max-w-2xl text-base text-neutral-600">{t.trust_intro}</p>
                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {pillars.map((p, i) => (
                            <div key={i} className="glass p-8" data-testid={`about-pillar-${i}`}>
                                <p.icon size={28} style={{ color: "var(--ls-terracotta)" }} />
                                <h3 className="mt-6 font-display text-xl font-bold">{p.title}</h3>
                                <p className="mt-3 text-sm text-neutral-500">{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PHILOSOPHY QUOTE */}
            <section className="mx-auto max-w-4xl px-6 py-20 lg:px-10 text-center">
                <blockquote className="font-display text-2xl font-bold italic text-neutral-700 sm:text-3xl">
                    &ldquo;{t.philosophy}&rdquo;
                </blockquote>
            </section>

            {/* CTA + IMAGE */}
            <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-5 flex flex-col justify-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight">{t.cta_title}</h2>
                        <p className="mt-4 text-base text-neutral-600">{t.cta_desc}</p>
                        <div className="mt-8">
                            <Link to="/servicio" className="btn-primary" data-testid="about-cta-service">
                                {t.cta_btn} <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="relative aspect-[16/9] overflow-hidden border border-black/10">
                            <img src={TEAM_IMG} alt="LS39 workshop team" className="h-full w-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICE LINKS */}
            <ServiceLinksBlock />
        </div>
    );
}
